import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom number stepper — neumorphic style, no native spinner.
 * Matches site design with smooth press animations on + / - buttons.
 */
function NumberStepper({
  value,
  onChange,
  min = 0,
  max = 999999,
  step = 1,
  name,
  id,
  label,
  placeholder,
  required,
  disabled,
  className = '',
  'aria-label': ariaLabel,
}) {
  const [focused, setFocused] = useState(false);
  const [inputText, setInputText] = useState(String(value));
  const inputRef = useRef(null);

  const num = typeof value === 'number' && !Number.isNaN(value) ? value : min;
  const clamped = Math.min(max, Math.max(min, num));

  useEffect(() => {
    if (!focused) setInputText(String(clamped));
  }, [clamped, focused]);

  const updateValue = useCallback(
    (next) => {
      const n = Math.min(max, Math.max(min, next));
      if (n !== clamped) {
        setInputText(String(n));
        onChange(n);
      }
    },
    [min, max, clamped, onChange]
  );

  const handleStepUp = () => {
    updateValue(clamped + step);
  };

  const handleStepDown = () => {
    updateValue(clamped - step);
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    setInputText(raw);
    if (raw === '' || raw === '-') {
      return;
    }
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      const n = Math.min(max, Math.max(min, parsed));
      onChange(n);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    setInputText(String(clamped));
    onChange(clamped);
  };

  const handleFocus = () => setFocused(true);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleStepUp();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleStepDown();
    }
  };

  const labelId = id ? `${id}-label` : undefined;

  return (
    <div className={`number-stepper ${className}`}>
      {label && (
        <label className="form-label" id={labelId} htmlFor={id || name}>
          {label}
        </label>
      )}
      <div
        className={`number-stepper__wrap ${focused ? 'number-stepper__wrap--focused' : ''}`}
        aria-label={ariaLabel || label}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="number-stepper__input"
          value={inputText}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          id={id || name}
          name={name}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          aria-labelledby={labelId}
          aria-valuenow={clamped}
          aria-valuemin={min}
          aria-valuemax={max}
        />
        <div className="number-stepper__controls" aria-hidden="true">
          <button
            type="button"
            className="number-stepper__btn number-stepper__btn--up"
            onClick={handleStepUp}
            disabled={disabled || clamped >= max}
            aria-label="Increase value"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
          <button
            type="button"
            className="number-stepper__btn number-stepper__btn--down"
            onClick={handleStepDown}
            disabled={disabled || clamped <= min}
            aria-label="Decrease value"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default NumberStepper;
